import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags/feature-flag.constants';
import { ITask, LanguagesService, ProjectService } from '@alpha-core/platform-ui';
import {
    ADMIN_INBOX_CONFIGS,
    INBOX_LIST_TYPE,
    InboxFilterType,
} from '@alpha-core/platform-ui/interfaces/admin-filter.interface';
import { FilterService } from '@alpha-core/platform-ui/services/filter.service';
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { MenuItem, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Menu } from 'primeng/menu';
import { OverlayPanel } from 'primeng/overlaypanel';
import { lastValueFrom } from 'rxjs';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AdminFilterService } from '../../../services/admin-filter.service';
import { FilterConfigService } from '../../../services/filter-config.service';
import { InboxService } from '../../../services/inbox.service';
import { userService } from '../../../services/user-service';
import { PrimeNGModules } from '../../../shared/alpha-shared.module';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { InboxSearchPipe } from './inbox-search.pipe';

interface Configs {
    name: string;
    key: 'tasks' | 'signals' | 'recovery';
    default: boolean;
    filterObject?: any;
}
@Component({
    selector: 'app-inbox-list',
    standalone: true,
    imports: [PrimeNGModules, TranslatePipe, InboxSearchPipe],
    templateUrl: './inbox-list.component.html',
})
export class InboxListComponent {
    private projectService = inject(ProjectService);
    private userService = inject(userService);
    private inboxService = inject(InboxService);
    private languageService = inject(LanguagesService);
    private featureFlagsService = inject(FeatureFlagsService);

    private filterConfigService = inject(FilterConfigService);
    private adminFilterService: AdminFilterService = inject(AdminFilterService);
    private filterService = inject(FilterService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private messageService = inject(MessageService);

    @Input() inboxtask: any;
    @Output() changeTask: EventEmitter<string> = new EventEmitter();
    taskConfigs: Array<ADMIN_INBOX_CONFIGS> = [
        { key: 'tasks', name: 'default-cm-tasks', default: false },
        { key: 'signals', name: 'default-cm-signals', default: false },
        { key: 'recovery', name: 'default-cm-re-attempt', default: false },
    ];
    items: MenuItem[] = [
        {
            label: 'default-cm-setDefault',
        },
        {
            label: 'default-cm-delete',
        },
    ];

    selectedTaskNames = {
        tasks: 'default-cm-tasks',
        signals: 'default-cm-signals',
        recovery: 'default-cm-re-attempt',
    };

    kababMenu!: any;
    searchInput: any = '';
    taskName: string = 'Tasks';
    keyName: string | any = 'tasks';
    filteredFilters: any;
    savedLists: Array<ADMIN_INBOX_CONFIGS> = [];
    savedListMenu!: Array<any>;
    @Input() selectedTask: ADMIN_INBOX_CONFIGS = this.taskConfigs[0];
    @Output() selectedTaskChange: EventEmitter<ADMIN_INBOX_CONFIGS> = new EventEmitter();
    destroyRef = inject(DestroyRef);
    private dialogService = inject(DialogService);
    adminFilters: Array<ADMIN_INBOX_CONFIGS> = [];
    adminFilterFeatureEnabled: boolean = false;
    defaultTaskName: string = '';

    constructor() {
        this.inboxService.onInboxRefresh$.pipe(takeUntilDestroyed()).subscribe((isRefresh) => {
            if (isRefresh) {
                this.getDefaultTask();
            }
        });
    }

    async ngOnInit() {
        this.projectService
            .getSavedList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((_) => {
                this.getSavedList();
                if (!this.selectedTask) {
                    this.selectedTask = this.selectedTask || this.taskConfigs[0];
                }
                this.selectedTaskChange.emit(this.selectedTask);
            });
        this.kababMenu = [
            {
                label: 'default-cm-setDefault',
                icon: '',
            },
        ];
        this.selectedTask = this.selectedTask || this.taskConfigs[0];
        const metadata = (await lastValueFrom(this.userService.getUserMetadata())).metadata;
        const project = this.projectService.fetchCurrentProject();
        this.adminFilterFeatureEnabled = this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_KEYS.ADMIN_FILTERS);
        if (this.adminFilterFeatureEnabled) {
            this.fetchAdminFilters();
        }
        this.taskConfigs.forEach((task) => {
            task.default = task.name === metadata[0].metadata?.adminDefaultTask?.[project.projectId]?.name;
            this.defaultTaskName = metadata[0].metadata?.adminDefaultTask?.[project.projectId]?.name;
        });
        this.filteredFilters = this.inboxtask[this.keyName]?.filters;
        this.taskConfigs = this.taskConfigs.filter((t) => this.inboxtask[t.key]?.enabled === true);
        this.selectedTaskChange.emit(this.selectedTask);
    }

    async fetchAdminFilters() {
        const inboxListTypes = [INBOX_LIST_TYPE.TASKS, INBOX_LIST_TYPE.SIGNALS, INBOX_LIST_TYPE.RECOVERY];
        const enabledListTypes = Object.values(inboxListTypes).filter((type) => this.inboxtask[type]?.enabled);
        this.adminFilters = await this.adminFilterService.fetchAdminListFilters(enabledListTypes);
    }

    getSavedList() {
        const projectId = this.projectService.fetchCurrentProject().projectId;
        this.userService.getUserMetadata().subscribe((res: any) => {
            const savedFilters = res.metadata[0]?.metadata?.adminSavedFilters?.[projectId] || null;
            if (savedFilters) {
                const savedLists: Array<ADMIN_INBOX_CONFIGS> = [];
                for (let [key, value] of Object.entries(savedFilters)) {
                    if (this.inboxtask?.[key]?.enabled) savedLists.push(...(value as Array<ADMIN_INBOX_CONFIGS>));
                }
                this.savedLists = structuredClone(savedLists);
            }
        });
    }

    savedListDelete(event: any, data: any, index: number) {
        const projectId = this.projectService.fetchCurrentProject().projectId;
        this.userService.getUserMetadata().subscribe((res: any) => {
            const metadata = {
                ...res.metadata[0].metadata,
            };
            if (data.default) {
                delete metadata.adminDefaultTask[projectId];
            }
            if (metadata.adminSavedFilters?.[projectId]?.[data.key]) {
                metadata.adminSavedFilters[projectId][data.key] = metadata.adminSavedFilters?.[projectId]?.[
                    data.key
                ].filter((value: any) => value.name !== data.name);
            }
            this.userService.updateUserMetadata(metadata).subscribe({
                complete: () => {
                    this.savedLists.splice(index, 1);
                    this.getDefaultTask();
                },
                error: (err) => {
                    console.error(err);
                },
            });
        });
    }

    savedListMenuClick(event: any, action: any, overlay: OverlayPanel, savedList: Menu) {
        event.stopPropagation();
        if (action.key === 'delete') {
            this.dialogService
                .open(ConfirmDialogComponent, {
                    width: '30rem',
                    header: this.languageService.getTranslation('default-cm-confirm') || 'Confirm',
                    data: { description: `default-cm-deleteConfirmationMessage` },
                })
                .onClose.subscribe((res) => {
                    if (res) {
                        this.savedListDelete(event, action.config, action.index);
                        overlay?.hide();
                    }
                });
        } else if (action.key === 'setAsDefault') {
            this.setDefaultTask(action.config, action.index);
        }
        savedList?.hide();
    }

    handleTask(filter: any, index?: number, closeOverlay?: OverlayPanel) {
        if (this.selectedTask?.name === filter?.name) {
            closeOverlay?.hide();
            return;
        }
        this.selectedTask = structuredClone(filter);
        this.taskName = filter.name;
        this.projectService.changeTask(this.selectedTask);
        this.filterConfigService.editSubject.next({ filter: filter, index });
        this.filterService.inboxType = this.selectedTask.key;
        this.filterService.updateUrl(filter.filterObject, this.selectedTask as ITask, this.route);
        closeOverlay?.hide();
    }

    async handleAdminFilter(filter: ADMIN_INBOX_CONFIGS, index?: number, closeOverlay?: OverlayPanel) {
        if (this.selectedTask?.name === filter?.name || !filter.filterId) {
            closeOverlay?.hide();
            return;
        }
        const response = await lastValueFrom(this.adminFilterService.getFilterById(filter.filterId));

        const { additionalFilters = {}, filters = {} } = response || {};

        filter.filterObject = { ...additionalFilters, ...filters };
        this.selectedTask = structuredClone(filter);
        this.taskName = filter.name;
        this.projectService.changeTask(filter);
        this.filterConfigService.editSubject.next({ filter: filter, index });
        closeOverlay?.hide();
    }

    searchList() {
        const searchTerm = this.searchInput.toLowerCase();
        this.keyName = this.taskConfigs.find((task) => task.name === this.taskName)?.key;
        return (
            this.inboxtask[this.keyName]?.filters.filter((filter: any) =>
                filter.filterName.toLowerCase().includes(searchTerm),
            ) ?? []
        );
    }

    updateFilteredFilters() {
        this.filteredFilters = this.searchList();
    }

    setDefaultTask(task: any, index: number, closeOverlay?: OverlayPanel) {
        const projectId = this.projectService.fetchCurrentProject().projectId;
        this.userService.getUserMetadata().subscribe((res: any) => {
            task.default = true;
            const metadata = {
                ...res.metadata[0].metadata,
                adminDefaultTask: {
                    ...res.metadata[0].metadata?.adminDefaultTask,
                    [projectId]: task,
                },
            };
            if (metadata?.adminSavedFilters?.[projectId]) {
                metadata.adminSavedFilters[projectId] = this.updateDefaultTaskStatus(
                    metadata.adminSavedFilters[projectId] || {},
                    task.name,
                );
            }
            this.userService.updateUserMetadata(metadata).subscribe((res) => {
                this.getSavedList();
                this.getDefaultTask();
            });
        });
        this.updateAdminDefaultTaskStatus(task);
        this.defaultTaskName = task.name;

        const taskName = this.languageService.getTranslation(task.name) || task.name;
        this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: (taskName || '').trim() + ' set as default list',
        });
    }

    updateAdminDefaultTaskStatus(task: any) {
        const isAdminTask = task.type === InboxFilterType.ADMIN_FILTER;
        this.adminFilters.forEach((filter) => {
            filter.default = isAdminTask && filter.name === task.name;
        });
    }

    updateDefaultTaskStatus(savedFilters: Record<string, ISavedFilter[]>, defaultTaskName: string) {
        Object.keys(savedFilters).forEach((key) => {
            const taskArray = savedFilters[key];
            if (Array.isArray(taskArray)) {
                savedFilters[key] = taskArray.map((customFilter) => ({
                    ...customFilter,
                    default: customFilter.name === defaultTaskName,
                }));
            }
        });
        return savedFilters;
    }

    getDefaultTask() {
        const projectId = this.projectService.fetchCurrentProject().projectId;
        this.userService.getUserMetadata().subscribe((res: any) => {
            const defaultTask = res.metadata[0]?.metadata?.adminDefaultTask
                ? res.metadata[0]?.metadata?.adminDefaultTask[projectId]
                : null;
            const filter = this.adminFilters.find((f) => f.name === defaultTask.name);
            if (defaultTask && (filter || defaultTask.type !== InboxFilterType.ADMIN_FILTER)) {
                this.defaultTaskName = defaultTask.name;
                this.taskConfigs.forEach((task) => {
                    task.default = task.name === defaultTask.name;
                });
                this.savedLists.forEach((list) => {
                    list.default = list.name === defaultTask.name;
                    if (list.default) {
                        this.selectedTask = list;
                        defaultTask.filterObject = list.filterObject;
                    }
                });
                const checkTaskEnable =
                    this.taskConfigs.some((t) => t.key === defaultTask.key) ||
                    this.savedLists.some((t) => t.name === defaultTask.name);

                if (defaultTask.type === InboxFilterType.ADMIN_FILTER) {
                    return this.handleAdminFilter(defaultTask);
                } else if (checkTaskEnable) {
                    return this.handleTask(defaultTask);
                }
            }
            const enabledTaskConfig = this.taskConfigs.find((f) => this.inboxtask?.[f?.['key']]?.enabled);
            this.handleTask(enabledTaskConfig);
        });
    }

    makeSavedListMenu(config: ADMIN_INBOX_CONFIGS, elem: any, event: any, index: number, defaultListMenu?: Menu) {
        event.stopPropagation();
        const disabled =
            config.default || (config.type === InboxFilterType.ADMIN_FILTER && config.name === this.defaultTaskName);
        this.savedListMenu = [
            {
                label: 'default-cm-setDefault',
                key: 'setAsDefault',
                disabled,
                icon: '',
                config,
                index,
            },
        ];
        if (config.type !== InboxFilterType.ADMIN_FILTER) {
            this.savedListMenu.push({
                label: 'default-cm-delete',
                key: 'delete',
                icon: '',
                config,
                index,
            });
        }
        defaultListMenu?.hide();
        elem.toggle(event);
    }

    makeDefaultListMenu(
        task: ADMIN_INBOX_CONFIGS,
        event: any,
        index: number,
        defaultListMenu: Menu,
        savedListMenu?: Menu,
    ) {
        event.stopPropagation();
        this.kababMenu = [
            {
                label: 'default-cm-setDefault',
                disabled: task.default,
                icon: '',
                task,
                index,
            },
        ];
        savedListMenu?.hide();
        defaultListMenu?.toggle(event);
    }
}

interface ISavedFilter {
    name: string;
    key: string;
    default: boolean;
    filterObject: { [key: string]: any };
}
